import { ConvexError } from 'convex/values';

export type LiveVoiceTranscriptFragment = {
  eventId: string;
  role: 'user' | 'assistant';
  text: string;
  startMs: number;
  endMs: number;
};

export function liveVoiceTranscriptFragment(
  event: Record<string, unknown>
): LiveVoiceTranscriptFragment | null {
  const role =
    event.type === 'session.input_transcript.delta'
      ? 'user'
      : event.type === 'session.output_transcript.delta'
        ? 'assistant'
        : null;
  if (
    !role ||
    typeof event.event_id !== 'string' ||
    !event.event_id ||
    event.event_id.length > 256 ||
    typeof event.delta !== 'string' ||
    !event.delta ||
    event.delta.length > 8192 ||
    typeof event.start_ms !== 'number' ||
    !Number.isFinite(event.start_ms) ||
    event.start_ms < 0 ||
    typeof event.end_ms !== 'number' ||
    !Number.isFinite(event.end_ms) ||
    event.end_ms < event.start_ms
  )
    return null;
  return {
    eventId: event.event_id,
    role,
    text: event.delta,
    startMs: event.start_ms,
    endMs: event.end_ms,
  };
}

const RETRY_DELAYS_MS = [300, 900, 1800];
const MAX_PENDING_FRAGMENTS = 240;
const REQUEST_TIMEOUT_MS = 2000;
const DRAIN_TIMEOUT_MS = 12_000;
// Provider close can take 15s. Leave 1s before the UI's 18s teardown and stay
// inside the server's 30s post-terminal admission window.
const TERMINAL_DRAIN_TIMEOUT_MS = 17_000;
const INCOMPLETE_RESERVE_MS = 500;

function isPermanentTranscriptFailure(error: unknown): boolean {
  return (
    error instanceof ConvexError &&
    typeof error.data === 'string' &&
    [
      'INVALID_VOICE_TRANSCRIPT',
      'VOICE_TRANSCRIPT_EVENT_COLLISION',
      'VOICE_CONVERSATION_UNAVAILABLE',
    ].includes(error.data)
  );
}

/** Bounded, ordered uploads share the call's final-report drain. */
export function createLiveVoiceTranscriptWriter(args: {
  persist: (fragments: LiveVoiceTranscriptFragment[]) => Promise<unknown>;
  markIncomplete?: () => Promise<unknown>;
  pending: Set<Promise<void>>;
  schedule?: (run: () => void) => void;
  sleep?: (delayMs: number) => Promise<void>;
}) {
  const queue: LiveVoiceTranscriptFragment[] = [];
  let inFlight: Promise<boolean> | null = null;
  let uploading = 0;
  let incomplete = false;
  let markedIncomplete = false;
  let terminalDeadline: number | null = null;
  let scheduled: { release: () => void } | null = null;
  const sleep =
    args.sleep ??
    ((delay) => new Promise<void>((resolve) => setTimeout(resolve, delay)));

  async function save(upload: () => Promise<unknown>, deadline: number) {
    for (let attempt = 0; ; attempt += 1) {
      const remaining = deadline - Date.now();
      if (remaining <= 0) return false;
      let timeout: ReturnType<typeof setTimeout> | undefined;
      try {
        await Promise.race([
          upload(),
          new Promise<never>((_, reject) => {
            timeout = setTimeout(
              () => reject(new Error('Transcript upload timed out')),
              Math.min(REQUEST_TIMEOUT_MS, remaining)
            );
          }),
        ]);
        return true;
      } catch (error) {
        const delay = RETRY_DELAYS_MS[attempt];
        if (
          isPermanentTranscriptFailure(error) ||
          delay === undefined ||
          Date.now() + delay >= deadline
        )
          return false;
        await sleep(delay);
      } finally {
        if (timeout !== undefined) clearTimeout(timeout);
      }
    }
  }

  function flush(): Promise<boolean> {
    scheduled?.release();
    scheduled = null;
    if (inFlight) return inFlight;
    const deadline = terminalDeadline ?? Date.now() + DRAIN_TIMEOUT_MS;
    const work = Promise.resolve()
      .then(async () => {
        do {
          while (queue.length) {
            const batch = queue.splice(0, 24);
            uploading = batch.length;
            if (
              !(await save(
                () => args.persist(batch),
                deadline - INCOMPLETE_RESERVE_MS
              ))
            )
              incomplete = true;
            uploading = 0;
          }
          if (incomplete && !markedIncomplete && args.markIncomplete) {
            markedIncomplete = await save(args.markIncomplete, deadline);
            if (!markedIncomplete)
              console.warn('[voice-call] Transcript completion unconfirmed');
          }
        } while (queue.length);
        return !incomplete;
      })
      .finally(() => {
        inFlight = null;
        if (queue.length) void flush();
      });
    inFlight = work;
    const tracked = work.then(() => undefined);
    args.pending.add(tracked);
    void tracked.finally(() => {
      args.pending.delete(tracked);
    });
    return work;
  }

  return {
    append(fragment: LiveVoiceTranscriptFragment) {
      if (queue.length + uploading >= MAX_PENDING_FRAGMENTS) {
        incomplete = true;
      } else {
        queue.push(fragment);
      }
      if (scheduled || inFlight) return;
      let release: () => void = () => {};
      const pending = new Promise<void>((resolve) => {
        release = resolve;
      });
      const ticket = {
        release: () => {
          args.pending.delete(pending);
          release();
        },
      };
      scheduled = ticket;
      args.pending.add(pending);
      (
        args.schedule ??
        ((run) => {
          setTimeout(run, 400);
        })
      )(() => {
        if (scheduled === ticket) void flush();
      });
    },
    flush,
    finish(finalized?: boolean) {
      terminalDeadline ??= Date.now() + TERMINAL_DRAIN_TIMEOUT_MS;
      if (finalized === false) incomplete = true;
      return flush();
    },
  };
}
